import type { FC, ReactNode } from "react";
import { createContext, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { THEMES } from "../constants";
import Settings from "../types/settings";
import { restoreSettings } from "./utils/restoreSettings";
import { storeSettings } from "./utils/storeSettings";

export interface SettingsContextValue {
  settings: Settings;
  saveSettings: (update: Settings) => void;
}

interface SettingsProviderProps {
  children?: ReactNode;
}

const initialSettings: Settings = {
  compact: false,
  direction: "ltr",
  responsiveFontSizes: true,
  roundedCorners: true,
  theme: THEMES.LIGHT,
  selectedOrganization: null,
  clientId: null,
};

const SettingsContext = createContext<SettingsContextValue>({
  settings: initialSettings,
  saveSettings: () => {},
});

export const SettingsProvider: FC<SettingsProviderProps> = (props) => {
  const { children } = props;
  const [settings, setSettings] = useState<Settings>({
    ...initialSettings,
    clientId: uuidv4(),
  });

  useEffect(() => {
    const restoredSettings = restoreSettings();

    if (restoredSettings) {
      setSettings({ ...restoredSettings, clientId: uuidv4() });
    }
  }, []);

  const saveSettings = (updatedSettings: Settings): void => {
    setSettings(updatedSettings);
    storeSettings(updatedSettings);
  };

  const contextValue = useMemo(
    () => ({
      settings,
      saveSettings,
    }),
    [settings, saveSettings],
  );

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};

export const SettingsConsumer = SettingsContext.Consumer;

export default SettingsContext;
